import { type ReactNode, useMemo, useState } from 'react';

const DEFAULT_EMAIL_ROLE: 'to' | 'cc' = 'cc';
import { T, useGT } from 'gt-react';
import { PlusIcon, SpinnerIcon, XIcon } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { validateEmail } from '@/utils/emailValidation';

export interface EmailRecipientContact {
  id: string;
  email: string;
  name?: string | null;
  badges?: ReactNode;
}

export interface EmailRecipientsListProps {
  recipients: EmailRecipientContact[];
  selectedRoles: Map<string, 'to' | 'cc'>;
  onToggleRole: (email: string) => void;
  onRemove?: (contact: EmailRecipientContact) => void;
  availableContacts?: EmailRecipientContact[];
  onAddExisting?: (contact: EmailRecipientContact) => void;
  onAddNew?: (email: string) => void | Promise<void>;
  isLoading?: boolean;
  isAdding?: boolean;
  addButtonLabel?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  className?: string;
  disabled?: boolean;
}

export function EmailRecipientsList({
  recipients,
  selectedRoles,
  onToggleRole,
  onRemove,
  availableContacts = [],
  onAddExisting,
  onAddNew,
  isLoading = false,
  isAdding = false,
  addButtonLabel = 'Add recipients',
  searchPlaceholder = 'Search contacts or enter email...',
  emptyMessage = 'No recipients selected yet.',
  className,
  disabled = false,
}: EmailRecipientsListProps) {
  const gt = useGT();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const searchNormalized = search.trim().toLowerCase();
  const searchIsValidEmail = validateEmail(searchNormalized);

  const selectedEmails = useMemo(
    () => new Set(recipients.map((r) => r.email.toLowerCase())),
    [recipients],
  );

  const pickableContacts = useMemo(
    () =>
      availableContacts.filter(
        (c) => !selectedEmails.has(c.email.toLowerCase()),
      ),
    [availableContacts, selectedEmails],
  );

  const filteredPickable = useMemo(() => {
    if (!searchNormalized) return pickableContacts;
    return pickableContacts.filter(
      (c) =>
        (c.name ?? '').toLowerCase().includes(searchNormalized) ||
        c.email.toLowerCase().includes(searchNormalized),
    );
  }, [pickableContacts, searchNormalized]);

  const searchMatchesExisting = useMemo(() => {
    if (!searchIsValidEmail) return false;
    return (
      selectedEmails.has(searchNormalized) ||
      availableContacts.some((c) => c.email.toLowerCase() === searchNormalized)
    );
  }, [searchIsValidEmail, searchNormalized, selectedEmails, availableContacts]);

  const canAddNew = !!onAddNew && searchIsValidEmail && !searchMatchesExisting;

  function closeAddPopover() {
    setOpen(false);
    setSearch('');
  }

  function handlePickExisting(contact: EmailRecipientContact) {
    onAddExisting?.(contact);
    closeAddPopover();
  }

  async function handleAddNew() {
    if (!canAddNew) return;
    try {
      await onAddNew?.(searchNormalized);
      closeAddPopover();
    } catch {
      // caller is responsible for error surfacing; leave popover open
    }
  }

  return (
    <div
      className={cn('rounded-md border divide-y overflow-hidden', className)}
    >
      {isLoading ? (
        <div className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground">
          <SpinnerIcon className="h-4 w-4 animate-spin" />
          <T>Loading contacts...</T>
        </div>
      ) : recipients.length === 0 ? (
        <div className="px-3 py-2 text-sm text-muted-foreground">
          {emptyMessage}
        </div>
      ) : (
        recipients.map((contact) => {
          const role =
            selectedRoles.get(contact.email.toLowerCase()) ??
            DEFAULT_EMAIL_ROLE;
          const hasDistinctName =
            !!contact.name &&
            contact.name.trim().toLowerCase() !== contact.email.toLowerCase();
          return (
            <div
              key={contact.id}
              className="group flex items-center gap-3 px-3 py-2"
            >
              <button
                type="button"
                onClick={() => onToggleRole(contact.email.toLowerCase())}
                disabled={disabled}
                className={cn(
                  'shrink-0 min-w-[28px] rounded px-1.5 py-0.5 text-center text-xs font-medium cursor-pointer transition-colors',
                  role === 'to'
                    ? 'bg-blue-100 text-blue-800 hover:bg-blue-200'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200',
                )}
              >
                {role === 'to' ? <T>To</T> : <T>Cc</T>}
              </button>
              <div className="flex min-w-0 flex-1 items-baseline gap-2">
                {hasDistinctName ? (
                  <>
                    <span className="shrink-0 text-sm font-medium group-hover:hidden">
                      {contact.name}
                    </span>
                    <span
                      className={cn(
                        'truncate text-xs text-muted-foreground',
                        'group-hover:overflow-visible group-hover:whitespace-normal group-hover:break-all',
                        'group-hover:text-sm group-hover:font-medium group-hover:text-foreground',
                      )}
                    >
                      {contact.email}
                    </span>
                  </>
                ) : (
                  <span
                    className={cn(
                      'truncate text-sm font-medium',
                      'group-hover:overflow-visible group-hover:whitespace-normal group-hover:break-all',
                    )}
                  >
                    {contact.email}
                  </span>
                )}
              </div>
              {contact.badges && (
                <div className="flex shrink-0 items-center gap-1">
                  {contact.badges}
                </div>
              )}
              {onRemove && (
                <button
                  type="button"
                  onClick={() => onRemove(contact)}
                  disabled={disabled}
                  aria-label={gt('Remove {email}', {
                    email: contact.email,
                    context: 'button to remove this email recipient',
                  })}
                  className="shrink-0 text-muted-foreground hover:text-destructive"
                >
                  <XIcon className="h-4 w-4" />
                </button>
              )}
            </div>
          );
        })
      )}

      <Popover
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) setSearch('');
        }}
      >
        <PopoverTrigger asChild>
          <button
            type="button"
            disabled={disabled}
            className="flex w-full items-center justify-center gap-1.5 bg-muted/30 px-3 py-1.5 text-sm font-medium text-primary hover:bg-muted/60 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <PlusIcon className="h-4 w-4" />
            {addButtonLabel}
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-80 p-0" align="center">
          <Command shouldFilter={false}>
            <CommandInput
              placeholder={searchPlaceholder}
              value={search}
              onValueChange={setSearch}
            />
            <CommandList>
              <CommandEmpty>
                {canAddNew ? (
                  <AddNewRow
                    email={searchNormalized}
                    onAdd={handleAddNew}
                    isAdding={isAdding}
                  />
                ) : searchIsValidEmail && searchMatchesExisting ? (
                  <div className="px-2 py-3 text-sm text-muted-foreground">
                    <T>That email is already in the list.</T>
                  </div>
                ) : searchNormalized.length > 0 ? (
                  <div className="px-2 py-3 text-sm text-muted-foreground">
                    <T>No matches. Enter a full email to add a new contact.</T>
                  </div>
                ) : (
                  <div className="px-2 py-3 text-sm text-muted-foreground">
                    <T>Start typing to search.</T>
                  </div>
                )}
              </CommandEmpty>
              {filteredPickable.length > 0 && (
                <CommandGroup heading={gt('Contacts')}>
                  {filteredPickable.map((c) => {
                    const hasDistinctName =
                      !!c.name &&
                      c.name.trim().toLowerCase() !== c.email.toLowerCase();
                    return (
                      <CommandItem
                        key={c.id}
                        value={`${c.name ?? ''} ${c.email}`}
                        onSelect={() => handlePickExisting(c)}
                      >
                        <div
                          className="flex min-w-0 flex-1 items-baseline gap-2"
                          title={c.email}
                        >
                          {hasDistinctName ? (
                            <>
                              <span className="shrink-0 text-sm font-medium">
                                {c.name}
                              </span>
                              <span className="truncate text-xs text-muted-foreground">
                                {c.email}
                              </span>
                            </>
                          ) : (
                            <span className="truncate text-sm font-medium">
                              {c.email}
                            </span>
                          )}
                        </div>
                        {c.badges && (
                          <div className="ml-auto flex shrink-0 items-center gap-1">
                            {c.badges}
                          </div>
                        )}
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              )}
              {canAddNew && filteredPickable.length > 0 && (
                <CommandGroup heading={gt('New')}>
                  <AddNewItem
                    email={searchNormalized}
                    onAdd={handleAddNew}
                    isAdding={isAdding}
                  />
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}

function AddNewBody({
  email,
  isAdding,
  iconClassName,
  textAlign,
}: {
  email: string;
  isAdding: boolean;
  iconClassName: string;
  textAlign?: 'text-left';
}) {
  const Icon = isAdding ? SpinnerIcon : PlusIcon;
  return (
    <>
      <Icon
        className={cn(
          'h-4 w-4 shrink-0',
          iconClassName,
          isAdding && 'animate-spin',
        )}
      />
      <span className="shrink-0">
        <T>Add new contact:</T>
      </span>
      <span
        className={cn('ml-1 min-w-0 flex-1 truncate font-medium', textAlign)}
      >
        {email}
      </span>
    </>
  );
}

function AddNewRow({
  email,
  onAdd,
  isAdding,
}: {
  email: string;
  onAdd: () => void;
  isAdding: boolean;
}) {
  return (
    <div className="p-2">
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="w-full justify-start overflow-hidden"
        onClick={onAdd}
        disabled={isAdding}
        title={email}
      >
        <AddNewBody
          email={email}
          isAdding={isAdding}
          iconClassName="mr-1.5"
          textAlign="text-left"
        />
      </Button>
    </div>
  );
}

function AddNewItem({
  email,
  onAdd,
  isAdding,
}: {
  email: string;
  onAdd: () => void;
  isAdding: boolean;
}) {
  return (
    <CommandItem
      value={`__add_new__${email}`}
      onSelect={onAdd}
      disabled={isAdding}
      title={email}
    >
      <AddNewBody email={email} isAdding={isAdding} iconClassName="mr-2" />
    </CommandItem>
  );
}
